import { resolveSidebar, type AnySidebar, type Sidebar } from "@omnabase/shared";
import { forSaving } from "./sidebarEdits";

/** The sidebar editor rework (2026-10-26, docs/briefs/sidebar-rework-round.md item 1 and "After the red team's review"; report findings 4 to 7):
 *  the rules of the column's store, as a plain module the hook in `sidebar.ts` wraps (pure of React and the network: `save` is handed in).
 *
 *  In plain words: a change draws at once and is saved a short moment later, one save for a burst of changes. The edited copy stays on screen
 *  while a save waits or is on its way. Afterwards a workspace copy replaces it only when its content is unknown to this tab: content this tab
 *  sent or was shown before is "old news" (a late download of an earlier save), only unknown content is another tab's or device's change.
 *  Content is compared after the server's own tidy-up (a blank name saved as a name, the record laid over today's column). A failed save keeps
 *  the edited copy (another tab's copy does not replace it either); the next change, or Done, saves the whole layout again.
 *  The customers sidebar round (2026-10-29, report finding 8): one sync per column; `resolve` is the column's own read (the Customers column:
 *  `resolveSidebar(stored, defaultCustomersSidebar(tags))`), used to compare copies and for "Reset to default"; without it the inbox's. */

export type SidebarSync<L extends AnySidebar = Sidebar> = {
  /** what the column draws, given the workspace's copy right now */
  shown(server: L): L;
  /** draw `next` now and save it a moment later */
  change(next: L): void;
  /** "Reset to default": today's column now, the stored layout forgotten */
  reset(): void;
  /** send what waits now (Done, Escape, leaving the page); also retries a failed save */
  flush(keepalive?: boolean): Promise<void>;
  /** whether a save waits or is on its way */
  pending(): boolean;
  /** how many saves have settled: the counts follow the entries the column shows */
  saved(): number;
  /** goes up whenever what the column draws or `saved` changed (the hook's snapshot) */
  version(): number;
  /** told after every change of what the column draws or of `saved` */
  subscribe(listener: () => void): () => void;
};
export type SidebarSyncOptions<L extends AnySidebar = Sidebar> = { save: (body: L | null, keepalive?: boolean) => Promise<unknown>; delayMs?: number; /** the column's read (the inbox's when left out) */ resolve?: (stored: unknown) => L };

/** JSON with the keys sorted, so two copies of the same content compare equal whatever order the database gave their keys */
function canonical(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(canonical).join(",")}]`;
  if (v && typeof v === "object") return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${canonical((v as Record<string, unknown>)[k])}`).join(",")}}`;
  return JSON.stringify(v);
}
export function createSidebarSync<L extends AnySidebar = Sidebar>({ save, delayMs = 400, resolve = (stored) => resolveSidebar(stored) as L }: SidebarSyncOptions<L>): SidebarSync<L> {
  /** a workspace copy's content (read again through the column's rules, so both sides are normalised alike) */
  const contentOf = (server: unknown) => { const r = resolve(server); return canonical({ ...r, removed: [...(r.removed ?? [])].sort() }); };
  /** a layout's content as the server would answer it after storing it */
  const contentOfSent = (body: L | null) => contentOf(resolve(body === null ? null : forSaving(body)));
  let edited: L | null = null;
  let waiting: { body: L | null } | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let onTheWay = 0;
  let unsaved = false; // the last settled save failed: keep the edited copy until a save goes through
  let settled = 0;
  let sentCount = 0;
  let version = 0;
  const known = new Set<string>();
  const listeners = new Set<() => void>();
  const tell = () => { version += 1; for (const l of listeners) l(); };

  async function send(keepalive = false): Promise<void> {
    if (timer) { clearTimeout(timer); timer = null; }
    const job = waiting ?? (unsaved && edited ? { body: edited } : null);
    if (!job) return;
    waiting = null; onTheWay += 1; const mine = ++sentCount;
    known.add(contentOfSent(job.body));
    try { await save(job.body === null ? null : forSaving(job.body), keepalive); if (mine === sentCount) unsaved = false; }
    catch { if (mine === sentCount) unsaved = true; }
    finally { onTheWay -= 1; settled += 1; tell(); }
  }
  function queue(next: L, body: L | null) {
    edited = next; waiting = { body };
    known.add(contentOfSent(body));
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void send(), delayMs);
    tell();
  }
  function shown(server: L): L {
    const content = contentOf(server);
    if (!edited) { known.add(content); return server; }
    if (waiting || onTheWay > 0 || unsaved || known.has(content)) return edited;
    edited = null; known.add(content); // another tab's or device's change
    return server;
  }
  return {
    shown,
    change: (next) => queue(next, next),
    reset: () => queue(resolve(null), null),
    flush: (keepalive) => send(keepalive),
    pending: () => !!waiting || onTheWay > 0,
    saved: () => settled,
    version: () => version,
    subscribe: (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
  };
}
